import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import {
  LayoutDashboard,
  Building2,
  Layers,
  Shield,
  LogOut,
  Settings,
  PhoneCall,
  LogIn,
  Users,
  Mail,
  ClipboardCheck,
  Search,
  Contact,
  UserCheck,
  Bell,
  BellRing,
  ShieldAlert,
  UserCheck2,
  Clock,
  Receipt,
  DollarSign,
  LayoutGrid,
  CalendarDays,
  FileText,
  Wrench,
  MessageSquare,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ThemeToggle } from "@/components/theme-toggle";
import { PlatformCreditPill } from "@/components/platform/platform-credit-pill";
import { cn } from "@/lib/utils";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { formatDistanceToNow } from "date-fns";
import {
  canRoleAccessModule,
  navHrefToModule,
} from "@shared/platform/role-bundles";
import type { PlatformUserRole } from "@shared/schema";

interface PlatformUser {
  id: string;
  email: string;
  name: string;
  role: PlatformUserRole;
  mustResetPassword?: boolean;
}

interface PlatformSession {
  impersonating: {
    workspaceId: string;
    userId: string;
  } | null;
}

interface PlatformLayoutProps {
  children: React.ReactNode;
  fullBleed?: boolean;
}

interface NavItem {
  href: string;
  label: string;
  icon: React.ElementType;
  badge?: "pii";
}

interface NavSection {
  heading: string | null;
  items: NavItem[];
}

// Task #124 — Platform Admin chrome feels like a workspace sidebar:
// a top "Overview" entry, then an "Administration" group containing
// the super-admin tools (same items as before, just labelled).
const navSections: NavSection[] = [
  {
    heading: null,
    items: [
      { href: "/platform/dashboard", label: "Dashboard", icon: LayoutDashboard },
      { href: "/platform/prospects", label: "Prospects", icon: Contact },
      { href: "/platform/customers", label: "Customers", icon: UserCheck },
      { href: "/platform/invoices", label: "Invoices", icon: Receipt },
      { href: "/platform/billing", label: "Billing", icon: DollarSign },
      { href: "/platform/financial-audit", label: "Financial Audit", icon: FileText },
      { href: "/platform/lead-audit", label: "Lead & Client Audit", icon: FileText },
      { href: "/platform/event-audit", label: "Event Audit Log", icon: FileText },
    ],
  },
  {
    heading: "Engineering",
    items: [
      { href: "/platform/feedback", label: "Beta Feedback", icon: MessageSquare },
      { href: "/platform/engineer-reports", label: "Engineering Log", icon: Wrench },
    ],
  },
  {
    heading: "Administration",
    items: [
      { href: "/platform/admin", label: "Super Admin", icon: LayoutGrid, badge: "pii" },
    ],
  },
];

function getInitials(name: string, email: string) {
  if (name && name.trim()) {
    const parts = name.trim().split(" ");
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : parts[0].slice(0, 2).toUpperCase();
  }
  return email ? email[0].toUpperCase() : "A";
}

export function PlatformLayout({ children, fullBleed = false }: PlatformLayoutProps) {
  const [location, setLocation] = useLocation();

  const { data: me, isLoading, isError } = useQuery<{ user: PlatformUser; session: PlatformSession }>({
    queryKey: ["/api/platform/me"],
  });

  useEffect(() => {
    if (!isLoading && (isError || !me) && location !== "/platform/login") {
      setLocation("/platform/login?returnTo=" + encodeURIComponent(location));
    }
  }, [isLoading, isError, me, location, setLocation]);

  const role = me?.user?.role;

  // Task #249 — role-bundle nav filtering. super_admin sees everything;
  // other roles only see nav entries whose module is in their bundle.
  // Entries with no module mapping (none currently) are always shown.
  const visibleNavSections = role
    ? navSections
        .map((section) => ({
          ...section,
          items: section.items.filter((item) => {
            const moduleKey = navHrefToModule(item.href);
            if (!moduleKey) return true;
            return canRoleAccessModule(role, moduleKey);
          }),
        }))
        .filter((section) => section.items.length > 0)
    : navSections;

  const { data: piiPending } = useQuery<{ count: number }>({
    queryKey: ["/api/platform/pii-scans/pending-count"],
    refetchInterval: 60000,
    enabled: !!me,
  });

  const piiCount = piiPending?.count ?? 0;

  const handleLogout = async () => {
    try {
      await apiRequest("POST", "/api/platform/logout");
    } catch (error) {
      console.error("Logout error (continuing anyway):", error);
    }
    setLocation("/platform/login");
  };

  const handleStopImpersonation = async () => {
    try {
      await apiRequest("POST", "/api/platform/stop-impersonation");
      window.location.reload();
    } catch (error) {
      console.error("Stop impersonation error:", error);
    }
  };

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-800" />
      </div>
    );
  }

  if (!me && !isLoading) return null;

  // Task #249 — force password reset on first login. Until the temp
  // password is replaced, every platform page is replaced by the reset
  // form (the backend also hard-blocks all other endpoints).
  if (me?.user?.mustResetPassword) {
    return <ForcePasswordReset name={me.user.name} email={me.user.email} onSignOut={handleLogout} />;
  }

  const impersonating = me?.session?.impersonating;

  // Task #249 — deep-link page guard. A non-super_admin who navigates
  // directly to a module outside their bundle gets a 403 panel instead of
  // the page (the backend independently denies the underlying API calls).
  const currentModule = navHrefToModule(location);
  const pageDenied =
    !!role &&
    role !== "super_admin" &&
    !!currentModule &&
    !canRoleAccessModule(role, currentModule);

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Sidebar */}
      <div className="w-56 shrink-0 bg-white border-r border-gray-200 flex flex-col">
        {/* Brand header */}
        <div className="px-4 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-red-600 flex items-center justify-center shrink-0">
              <Shield className="h-4 w-4 text-white" />
            </div>
            <div>
              <div className="text-sm font-semibold text-gray-900">Clarik.io Platform</div>
              <div className="text-[10px] text-gray-400 uppercase tracking-wider">{me?.user?.name || ""}</div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-2 py-3 overflow-y-auto">
          {visibleNavSections.map((section, sectionIdx) => (
            <div key={section.heading ?? `section-${sectionIdx}`} className={sectionIdx > 0 ? "mt-4" : ""}>
              {section.heading && (
                <div
                  className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400"
                  data-testid={`nav-section-${section.heading.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {section.heading}
                </div>
              )}
              {section.items.map((item) => {
                const isActive = location.startsWith(item.href);
                return (
                  <Link key={item.href} href={item.href}>
                    <div
                      className={cn(
                        "flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer transition-colors text-sm mb-0.5",
                        isActive
                          ? "bg-gray-900 text-white font-medium"
                          : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                      )}
                      data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    >
                      <item.icon className={cn("h-4 w-4 shrink-0", isActive ? "text-white" : "text-gray-400")} />
                      <span className="flex-1 truncate">{item.label}</span>
                      {item.badge === "pii" && piiCount > 0 && (
                        <Badge className="bg-amber-500 text-black text-xs" data-testid="badge-pii-count">
                          {piiCount}
                        </Badge>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div className="border-t border-gray-100 p-3 space-y-1">
          {me && (
            <Link href="/platform/account">
              <div
                className={cn(
                  "flex items-center gap-2 px-1 py-1 rounded-lg cursor-pointer transition-colors",
                  location.startsWith("/platform/account") ? "bg-gray-100" : "hover:bg-gray-100",
                )}
                data-testid="link-platform-account"
              >
                <div className="w-7 h-7 rounded-full bg-gray-800 flex items-center justify-center text-white text-xs font-semibold shrink-0">
                  {getInitials(me.user.name, me.user.email)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-gray-900 truncate">
                    {me.user.name || me.user.email}
                  </div>
                  <div className="text-[10px] text-gray-400 truncate">{me.user.email}</div>
                </div>
                <Settings className="h-3.5 w-3.5 text-gray-400 shrink-0" />
              </div>
            </Link>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="w-full justify-start text-gray-500 hover:text-gray-900 text-xs h-8"
            data-testid="button-logout"
          >
            <LogOut className="h-3.5 w-3.5 mr-2" /> Sign out
          </Button>
        </div>
      </div>

      {/* Impersonation banner */}
      {impersonating && (
        <div className="absolute top-0 left-56 right-0 z-50 bg-amber-500 text-black text-xs px-4 py-1.5 flex items-center justify-between">
          <span>
            <LogIn className="h-3.5 w-3.5 inline mr-1" />
            Impersonating workspace <strong>{impersonating.workspaceId}</strong>
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleStopImpersonation}
            className="h-6 text-xs text-black hover:bg-amber-600"
            data-testid="button-stop-impersonation"
          >
            Stop
          </Button>
        </div>
      )}

      {/* Main content */}
      <div className={cn(
        "flex-1 flex flex-col",
        fullBleed ? "overflow-hidden" : "overflow-y-auto",
        impersonating && "pt-7",
      )}>
        {/* Top toolbar — page-level quick-nav, shown on every platform page.
            When impersonating, push it down 7 so it sits below the banner. */}
        <PlatformTopToolbar location={location} impersonating={!!impersonating} role={role} />
        {pageDenied ? (
          <AccessDeniedPanel />
        ) : fullBleed ? (
          <div className="flex-1 min-h-0 flex flex-col">
            {children}
          </div>
        ) : (
          <div className="p-6 max-w-6xl mx-auto w-full">
            {children}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Platform Notification Bell ───────────────────────────────────────────────

interface PlatformNotifSummary {
  count: number;
  breakdown: { betaApprovals: number; pendingSignups: number; piiFindings: number };
}

interface PlatformNotifItem {
  id: string;
  kind: string;
  title: string;
  body: string;
  linkUrl: string;
  severity: "info" | "warning" | "error";
  createdAt: string;
}

const kindIcon: Record<string, React.ElementType> = {
  pii: ShieldAlert,
  beta_approval: UserCheck2,
  pending_signup: Clock,
};

const severityDotCls: Record<string, string> = {
  info: "bg-blue-500",
  warning: "bg-amber-500",
  error: "bg-red-500",
};

function PlatformNotificationBell() {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();

  const { data: summary } = useQuery<PlatformNotifSummary>({
    queryKey: ["/api/platform/notifications/summary"],
    queryFn: async () => {
      const res = await fetch("/api/platform/notifications/summary", { credentials: "include" });
      if (!res.ok) return { count: 0, breakdown: { betaApprovals: 0, pendingSignups: 0, piiFindings: 0 } };
      return res.json();
    },
    refetchInterval: 30_000,
  });

  const { data: list, isLoading } = useQuery<{ items: PlatformNotifItem[] }>({
    queryKey: ["/api/platform/notifications"],
    queryFn: async () => {
      const res = await fetch("/api/platform/notifications", { credentials: "include" });
      if (!res.ok) return { items: [] };
      return res.json();
    },
    enabled: open,
  });

  useEffect(() => {
    if (open) {
      qc.invalidateQueries({ queryKey: ["/api/platform/notifications"] });
    }
  }, [open, qc]);

  const count = summary?.count ?? 0;
  const display = count > 99 ? "99+" : String(count);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-8 w-8 text-gray-500 hover:text-gray-900 hover:bg-gray-100"
          aria-label={`Platform notifications${count ? ` — ${count} pending` : ""}`}
          data-testid="button-platform-notifications-bell"
        >
          {count > 0 ? <BellRing className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
          {count > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 h-4 min-w-[1rem] px-1 text-[9px] font-bold leading-none rounded-full bg-red-600 text-white flex items-center justify-center"
              data-testid="badge-platform-notifications-count"
            >
              {display}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[380px] p-0 shadow-lg" data-testid="popover-platform-notifications">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b bg-gray-50">
          <div className="text-sm font-semibold text-gray-900">Platform Notifications</div>
          {summary && (
            <div className="flex items-center gap-2 text-xs text-gray-500">
              {summary.breakdown.piiFindings > 0 && (
                <span className="flex items-center gap-1">
                  <ShieldAlert className="h-3 w-3 text-red-500" />
                  {summary.breakdown.piiFindings} PII
                </span>
              )}
              {summary.breakdown.betaApprovals > 0 && (
                <span className="flex items-center gap-1">
                  <UserCheck2 className="h-3 w-3 text-blue-500" />
                  {summary.breakdown.betaApprovals} approval{summary.breakdown.betaApprovals !== 1 ? "s" : ""}
                </span>
              )}
              {summary.breakdown.pendingSignups > 0 && (
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3 text-amber-500" />
                  {summary.breakdown.pendingSignups} signup{summary.breakdown.pendingSignups !== 1 ? "s" : ""}
                </span>
              )}
            </div>
          )}
        </div>

        <ScrollArea className="max-h-[460px]">
          {isLoading && (
            <div className="px-4 py-8 text-center text-sm text-gray-400">Loading…</div>
          )}
          {!isLoading && (!list?.items || list.items.length === 0) && (
            <div className="px-4 py-10 text-center">
              <Bell className="h-8 w-8 mx-auto text-gray-300 mb-2" />
              <p className="text-sm text-gray-400">No pending items — all clear.</p>
            </div>
          )}
          {list?.items?.map((item) => {
            const KindIcon = kindIcon[item.kind] ?? Bell;
            const dotCls = severityDotCls[item.severity] ?? severityDotCls.info;
            const inner = (
              <div
                className="flex items-start gap-3 px-4 py-3 border-b last:border-b-0 hover:bg-gray-50 transition-colors cursor-pointer"
                data-testid={`platform-notif-item-${item.id}`}
              >
                <div className="shrink-0 mt-0.5">
                  <div className={cn("h-2 w-2 rounded-full mt-1.5", dotCls)} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <KindIcon className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                    <span className="text-sm font-medium text-gray-900 leading-snug truncate">{item.title}</span>
                  </div>
                  <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">{item.body}</p>
                  <p className="text-[10px] text-gray-400 mt-1">
                    {formatDistanceToNow(new Date(item.createdAt), { addSuffix: true })}
                  </p>
                </div>
              </div>
            );
            return (
              <Link key={item.id} href={item.linkUrl} onClick={() => setOpen(false)}>
                {inner}
              </Link>
            );
          })}
        </ScrollArea>

        {/* Footer */}
        <div className="border-t px-4 py-2 bg-gray-50 text-center">
          <Link href="/platform/security" onClick={() => setOpen(false)}>
            <Button variant="ghost" size="sm" className="text-xs h-7 text-gray-500 hover:text-gray-900">
              View all in Security hub
            </Button>
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Top horizontal toolbar inside the main content area. Sits above every
// platform page so quick-nav (Profiles, etc.) is always one click away.
interface TopToolbarItem {
  href: string;
  label: string;
  icon: React.ElementType;
}

const topToolbarItems: TopToolbarItem[] = [
  { href: "/platform/profiles", label: "Profiles", icon: Users },
  { href: "/platform/communications", label: "Communications", icon: Mail },
  { href: "/platform/approvals", label: "Action History", icon: ClipboardCheck },
  { href: "/platform/lead-gen", label: "Lead Gen", icon: Search },
];

function PlatformTopToolbar({
  location,
  impersonating,
  role,
}: {
  location: string;
  impersonating: boolean;
  role?: PlatformUserRole;
}) {
  const visibleItems = role
    ? topToolbarItems.filter((item) => {
        const moduleKey = navHrefToModule(item.href);
        if (!moduleKey) return true;
        return canRoleAccessModule(role, moduleKey);
      })
    : topToolbarItems;
  const canEmail = role ? canRoleAccessModule(role, "communications") : true;
  return (
    <div
      className={cn(
        "sticky z-30 bg-white/95 backdrop-blur border-b border-gray-200 px-6 py-2 flex items-center gap-1",
        impersonating ? "top-7" : "top-0",
      )}
      data-testid="toolbar-platform-top"
    >
      {visibleItems.map((item) => {
        const isActive = location.startsWith(item.href);
        return (
          <Link key={item.href} href={item.href}>
            <div
              className={cn(
                "flex items-center gap-1.5 px-2.5 py-1.5 rounded-md cursor-pointer text-xs font-medium transition-colors",
                isActive
                  ? "bg-gray-900 text-white"
                  : "text-gray-600 hover:bg-gray-100 hover:text-gray-900",
              )}
              data-testid={`toolbar-platform-${item.label.toLowerCase()}`}
            >
              <item.icon className="h-3.5 w-3.5" />
              <span>{item.label}</span>
            </div>
          </Link>
        );
      })}
      <div className="ml-auto flex items-center gap-1">
        {canEmail && (
          <Link href="/platform/email">
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "h-8 w-8",
                location.startsWith("/platform/email")
                  ? "text-gray-900 bg-gray-100"
                  : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
              )}
              aria-label="Email Hub"
              data-testid="button-platform-email-hub"
            >
              <Mail className="h-4 w-4" />
            </Button>
          </Link>
        )}
        <Button
          variant="ghost"
          size="icon"
          disabled
          className="h-8 w-8 text-gray-400"
          aria-label="Calendar Hub (unavailable in Platform Workspace)"
          title="Calendar Hub is available inside a workspace, not the Platform Workspace"
          data-testid="button-platform-calendar-hub"
        >
          <CalendarDays className="h-4 w-4" />
        </Button>
        <PlatformNotificationBell />
        <PlatformCreditPill />
        <ThemeToggle />
      </div>
    </div>
  );
}

// Task #249 — shown when a non-super_admin deep-links to a module outside
// their role bundle. The matching API calls are independently denied
// server-side; this is just the friendly UI.
function AccessDeniedPanel() {
  return (
    <div className="flex-1 flex items-center justify-center p-10" data-testid="panel-access-denied">
      <div className="max-w-md text-center">
        <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
          <ShieldAlert className="h-6 w-6 text-amber-600" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900">No access to this area</h2>
        <p className="text-sm text-gray-500 mt-1.5">
          Your platform role doesn’t include this module. If you think you need
          access, contact a Super Admin.
        </p>
        <Link href="/platform/dashboard">
          <Button className="mt-5" data-testid="button-back-dashboard">
            Back to Dashboard
          </Button>
        </Link>
      </div>
    </div>
  );
}

// Task #249 — full-screen forced password reset on first login. Uses the
// in-session change-password endpoint (temp password = current password).
function ForcePasswordReset({
  name,
  email,
  onSignOut,
}: {
  name: string;
  email: string;
  onSignOut: () => void;
}) {
  const { toast } = useToast();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 12) {
      toast({ title: "New password must be at least 12 characters", variant: "destructive" });
      return;
    }
    if (newPassword !== confirm) {
      toast({ title: "Passwords don’t match", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      await apiRequest("POST", "/api/platform/account/change-password", {
        currentPassword,
        newPassword,
      });
      toast({ title: "Password updated", description: "Welcome aboard." });
      await queryClient.invalidateQueries({ queryKey: ["/api/platform/me"] });
    } catch (err) {
      const raw = err instanceof Error ? err.message : String(err ?? "");
      const m = raw.match(/^\d+:\s*(.*)$/s);
      let msg = m ? m[1] : raw;
      try {
        const parsed = JSON.parse(msg);
        if (parsed?.message) msg = parsed.message;
      } catch {}
      toast({ title: msg || "Failed to update password", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="h-screen flex items-center justify-center bg-gray-50 p-4" data-testid="force-password-reset">
      <div className="w-full max-w-sm bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-8 h-8 rounded-lg bg-red-600 flex items-center justify-center shrink-0">
            <Shield className="h-4 w-4 text-white" />
          </div>
          <div>
            <div className="text-sm font-semibold text-gray-900">Set a new password</div>
            <div className="text-[11px] text-gray-400">{name || email}</div>
          </div>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          For your security, choose a new password before continuing. Enter the
          temporary password from your invite email, then your new one.
        </p>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="fpr-current">Temporary password</Label>
            <Input
              id="fpr-current"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              data-testid="input-current-password"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="fpr-new">New password</Label>
            <Input
              id="fpr-new"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              data-testid="input-new-password"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="fpr-confirm">Confirm new password</Label>
            <Input
              id="fpr-confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              data-testid="input-confirm-password"
            />
          </div>
          <Button type="submit" className="w-full" disabled={submitting} data-testid="button-set-password">
            {submitting ? "Updating…" : "Update password"}
          </Button>
        </form>
        <Button
          variant="ghost"
          size="sm"
          onClick={onSignOut}
          className="w-full mt-2 text-gray-500 text-xs"
          data-testid="button-force-reset-signout"
        >
          <LogOut className="h-3.5 w-3.5 mr-1.5" /> Sign out
        </Button>
      </div>
    </div>
  );
}
